import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  Clock,
  Plus,
  Wand2,
  ClipboardList,
  Calendar,
  Briefcase,
  Baby,
  Heart,
  Plane,
  Church,
  CheckCircle,
  Trash2,
  Edit,
  Star,
  ChevronRight,
  ChevronLeft,
  Send,
  Loader2,
  Bot,
  User,
  X,
  AlertCircle,
  GripVertical,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import type {
  TimeBudget,
  TimeBudgetReligiousHoliday,
  TimeBudgetJobSchedule,
  TimeBudgetChildCommitment,
  TimeBudgetPersonalActivity,
  TimeBudgetTravelEvent,
  TimeBudgetGoal,
  Goal,
} from "@shared/schema";
import { RELIGIOUS_HOLIDAYS } from "@shared/schema";
import { Target } from "lucide-react";
import { isFutureGoal } from "@/lib/goal-visibility";

const DAYS_OF_WEEK = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

interface TimeBudgetWithDetails extends TimeBudget {
  religiousHolidays?: TimeBudgetReligiousHoliday[];
  jobSchedules?: TimeBudgetJobSchedule[];
  childCommitments?: TimeBudgetChildCommitment[];
  personalActivities?: TimeBudgetPersonalActivity[];
  travelEvents?: TimeBudgetTravelEvent[];
  goalAllocations?: TimeBudgetGoal[];
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

// Helper to parse user time input like "6:30 AM", "6:30am", "06:30" into "HH:mm" format
function formatTimeForAPI(timeStr: string | undefined): string | undefined {
  if (!timeStr) return undefined;
  
  const input = timeStr.trim().toLowerCase();
  
  // Try to match various formats
  const ampmMatch = input.match(/^(\d{1,2}):?(\d{2})?\s*(am|pm)?$/);
  if (ampmMatch) {
    let hours = parseInt(ampmMatch[1]);
    const minutes = ampmMatch[2] ? parseInt(ampmMatch[2]) : 0;
    const period = ampmMatch[3];
    
    if (period === "pm" && hours < 12) hours += 12;
    if (period === "am" && hours === 12) hours = 0;
    
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }
  
  // Already in HH:mm format
  if (/^\d{2}:\d{2}$/.test(input)) {
    return input;
  }
  
  return timeStr;
}

function AITimeBudgetWizard({
  isOpen,
  onClose,
  onComplete
}: {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}) {
  const { toast } = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      role: "assistant",
      content: `Hi! I'm here to help you set up your time budget. This will help us understand how much realistic time you have available for your goals.

Once your time budget is approved, time blocking will be created automatically over the period you specify.

First, what time span would you like this budget to cover? Choose one: **month**, **quarter**, or **year**`,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [conversationStep, setConversationStep] = useState(0);
  const [collectedData, setCollectedData] = useState<Record<string, any>>({});
  const [isProcessing, setIsProcessing] = useState(false);

  const createTimeBudgetMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", "/api/time-budgets", data);
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Time Budget Created!",
        description: "Your time budget has been set up successfully."
      });
      queryClient.invalidateQueries({ queryKey: ["/api/time-budgets"] });
      onComplete();
      onClose();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create time budget",
        variant: "destructive"
      });
    }
  });

  const processUserInput = async (input: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: input,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsProcessing(true);

    const steps = [
      {
        field: "timeSpan",
        nextMessage: "Great! Now, when should this time budget start? Please enter a date (e.g., January 15, 2026 or 01/15/2026)"
      },
      {
        field: "startDate",
        nextMessage: "And when should it end? Please enter the end date (e.g., April 15, 2026 or 04/15/2026)"
      },
      {
        field: "endDate",
        nextMessage: "What would you like to name this time budget? (e.g., \"Q1 2026 Schedule\", \"My Weekly Routine\")"
      },
      {
        field: "name",
        nextMessage: "Now, what time do you typically wake up? (e.g., 6:30 AM or 06:30)"
      },
      {
        field: "wakeTime",
        nextMessage: "And what time do you usually go to bed? (e.g., 10:30 PM or 22:30)"
      },
      {
        field: "sleepTime",
        nextMessage: "When are you most productive? Choose one: morning, afternoon, or evening"
      },
      {
        field: "productiveTimeOfDay",
        nextMessage: "Do you have a job or regular work commitments? (yes/no)"
      },
      {
        field: "hasJob",
        nextMessage: "Do you have children with activities or commitments you need to plan around? (yes/no)"
      },
      {
        field: "hasChildren",
        nextMessage: (data: Record<string, any>) => data.hasChildren === "yes"
          ? "Please list your children's activities with days and times. For example:\n- School drop-off: Monday-Friday 7:30am-8:00am\n- Soccer practice: Tuesday & Thursday 4:00pm-5:30pm\n- Piano lessons: Wednesday 3:00pm-4:00pm"
          : "Do you observe any religious holidays that affect your schedule? (yes/no)"
      },
      {
        field: (data: Record<string, any>) => data.hasChildren === "yes" ? "childrenActivities" : "observesHolidays",
        nextMessage: (data: Record<string, any>) => data.hasChildren === "yes"
          ? "Do you observe any religious holidays that affect your schedule? (yes/no)"
          : "Are you working on any personal projects like writing a book or creating content? (yes/no)"
      },
      {
        field: (data: Record<string, any>) => data.hasChildren === "yes" ? "observesHolidays" : "hasProjects",
        nextMessage: (data: Record<string, any>) => data.hasChildren === "yes"
          ? "Are you working on any personal projects like writing a book or creating content? (yes/no)"
          : "Do you have any upcoming travel planned? (yes/no)"
      },
      {
        field: (data: Record<string, any>) => data.hasChildren === "yes" ? "hasProjects" : "hasTravel",
        nextMessage: (data: Record<string, any>) => data.hasChildren === "yes"
          ? "Do you have any upcoming travel planned? (yes/no)"
          : generateSummary(data)
      },
      {
        field: "hasTravel",
        nextMessage: (data: Record<string, any>) => generateSummary(data)
      }
    ];
    
    function generateSummary(data: Record<string, any>): string {
      return `Perfect! I have all the information I need. Here's a summary:

**Name:** ${data.name}
**Time Span:** ${data.timeSpan}
**Period:** ${data.startDate} to ${data.endDate}
**Wake Time:** ${data.wakeTime}
**Sleep Time:** ${data.sleepTime}
**Most Productive:** ${data.productiveTimeOfDay}
**Has Job:** ${data.hasJob}
${data.hasChildren === "yes" ? `**Has Children:** Yes\n**Children's Activities:**\n${data.childrenActivities || "None specified"}\n` : ""}

Once approved, time blocking will be automatically created for this period.

Would you like me to create this time budget? (yes/no)`;
    }

    setTimeout(() => {
      const currentStep = conversationStep;
      const newData = { ...collectedData };
      
      if (currentStep < steps.length) {
        const stepField = steps[currentStep].field;
        // Resolve field name - can be string or function
        const field = typeof stepField === "function" ? stepField(newData) : stepField;
        // Only lowercase yes/no fields, preserve original for names and dates
        const yesNoFields = ["hasJob", "hasChildren", "hasProjects", "hasTravel", "observesHolidays"];
        const lowercaseFields = ["timeSpan", "productiveTimeOfDay"];
        if (yesNoFields.includes(field) || lowercaseFields.includes(field)) {
          newData[field] = input.toLowerCase();
        } else {
          newData[field] = input;
        }
        setCollectedData(newData);
      }

      // After the last step (summary shown), user confirms with "yes" to create
      if (currentStep === steps.length && input.toLowerCase() === "yes") {
        const budgetData = {
          name: collectedData.name || "My Time Budget",
          timeSpan: collectedData.timeSpan || "month",
          startDate: collectedData.startDate || null,
          endDate: collectedData.endDate || null,
          wakeTime: formatTimeForAPI(collectedData.wakeTime) || "07:00",
          sleepTime: formatTimeForAPI(collectedData.sleepTime) || "22:00",
          productiveTimeOfDay: collectedData.productiveTimeOfDay || "morning",
          hasJob: collectedData.hasJob === "yes",
          hasChildren: collectedData.hasChildren === "yes",
        };
        
        // Show creating message
        const creatingMessage: Message = {
          id: Date.now().toString() + "_creating",
          role: "assistant",
          content: "Creating your time budget...",
          timestamp: new Date()
        };
        setMessages(prev => [...prev, creatingMessage]);
        
        createTimeBudgetMutation.mutate(budgetData);
        return;
      }
      
      // User said "no" at confirmation
      if (currentStep === steps.length && input.toLowerCase() === "no") {
        const cancelMessage: Message = {
          id: Date.now().toString() + "_cancel",
          role: "assistant",
          content: "No problem! Feel free to close this dialog and try again when you're ready.",
          timestamp: new Date()
        };
        setMessages(prev => [...prev, cancelMessage]);
        setIsProcessing(false);
        return;
      }

      if (currentStep < steps.length) {
        const assistantMessage: Message = {
          id: Date.now().toString() + "_assistant",
          role: "assistant",
          content: typeof steps[currentStep].nextMessage === "function" 
            ? (steps[currentStep].nextMessage as any)(newData)
            : steps[currentStep].nextMessage,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
        setConversationStep(prev => prev + 1);
      }

      setIsProcessing(false);
    }, 500);
  };

  const handleSend = () => {
    if (!inputValue.trim() || isProcessing) return;
    processUserInput(inputValue.trim());
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    // Refocus input after each message
    if (!isProcessing) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [messages, isProcessing]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Wand2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <DialogTitle>AI-Assisted Time Budget Setup</DialogTitle>
              <DialogDescription>
                I'll ask you a few questions to understand your schedule
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 px-6 py-4">
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 ${message.role === "user" ? "justify-end" : ""}`}
              >
                {message.role === "assistant" && (
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-primary/10">
                      <Bot className="w-4 h-4 text-primary" />
                    </AvatarFallback>
                  </Avatar>
                )}
                <div
                  className={`rounded-lg px-4 py-2 max-w-[80%] ${
                    message.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
                {message.role === "user" && (
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-secondary">
                      <User className="w-4 h-4" />
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        <div className="px-6 py-4 border-t">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type your response..."
              disabled={isProcessing || createTimeBudgetMutation.isPending}
              data-testid="input-ai-wizard-message"
            />
            <Button
              onClick={handleSend}
              disabled={!inputValue.trim() || isProcessing || createTimeBudgetMutation.isPending}
              data-testid="button-ai-wizard-send"
            >
              {isProcessing || createTimeBudgetMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const basicInfoSchema = z.object({
  name: z.string().min(1, "Name is required"),
  wakeTime: z.string().min(1, "Wake time is required"),
  sleepTime: z.string().min(1, "Sleep time is required"),
  productiveTimeOfDay: z.string().optional(),
});

const jobScheduleSchema = z.object({
  dayOfWeek: z.string().min(1, "Day is required"),
  startTime: z.string().min(1, "Start time is required"),
  endTime: z.string().min(1, "End time is required"),
  description: z.string().optional(),
});

const childCommitmentSchema = z.object({
  description: z.string().min(1, "Description is required"),
  dayOfWeek: z.string().min(1, "Day is required"),
  startTime: z.string().min(1, "Start time is required"),
  endTime: z.string().min(1, "End time is required"),
});

const personalActivitySchema = z.object({
  description: z.string().min(1, "Description is required"),
  dayOfWeek: z.string().min(1, "Day is required"),
  startTime: z.string().min(1, "Start time is required"),
  endTime: z.string().min(1, "End time is required"),
});

const travelEventSchema = z.object({
  description: z.string().min(1, "Description is required"),
  startDate: z.string().min(1, "Start date is required"),
  endDate: z.string().min(1, "End date is required"),
});

function CreateTimeBudgetDialog({
  isOpen,
  onClose,
  onComplete
}: {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}) {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [selectedHolidays, setSelectedHolidays] = useState<string[]>([]);
  const [hasJobAnswer, setHasJobAnswer] = useState<"yes" | "no" | null>(null);
  const [jobSchedules, setJobSchedules] = useState<z.infer<typeof jobScheduleSchema>[]>([]);
  const [childCommitments, setChildCommitments] = useState<z.infer<typeof childCommitmentSchema>[]>([]);
  const [personalActivities, setPersonalActivities] = useState<z.infer<typeof personalActivitySchema>[]>([]);
  const [travelEvents, setTravelEvents] = useState<z.infer<typeof travelEventSchema>[]>([]);
  
  // State for week day view in Child Commitments
  const [childActivityDescription, setChildActivityDescription] = useState("");
  const [childActivityDays, setChildActivityDays] = useState<{ dayOfWeek: string; startTime: string; endTime: string }[]>([]);
  const [collapsedChildActivities, setCollapsedChildActivities] = useState<Set<string>>(new Set());
  
  // State for week day view in Personal Activities
  const [personalActivityDescription, setPersonalActivityDescription] = useState("");
  const [personalActivityDays, setPersonalActivityDays] = useState<{ dayOfWeek: string; startTime: string; endTime: string }[]>([]);
  const [collapsedPersonalActivities, setCollapsedPersonalActivities] = useState<Set<string>>(new Set());
  
  // State for collapsible Travel Events
  const [collapsedTravelEvents, setCollapsedTravelEvents] = useState<Set<number>>(new Set());
  
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>([]);
  const [goalAllocations, setGoalAllocations] = useState<Array<{
    goalId: string;
    goalTitle: string;
    requiredMinutesPerWeek: number;
    allocatedMinutesPerWeek: number;
    preferredDays: string[];
    scheduledBlocks: Array<{ day: string; startTime: string; endTime: string }>;
  }>>([]);
  const [analysis, setAnalysis] = useState<GoalAllocationAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activityAdjustments, setActivityAdjustments] = useState<{
    sleepAdjustment: number;
    workAdjustment: number;
    childAdjustment: number;
    personalAdjustment: number;
    travelAdjustment: number;
  }>({ sleepAdjustment: 0, workAdjustment: 0, childAdjustment: 0, personalAdjustment: 0, travelAdjustment: 0 });
  const [goalRescopeDrafts, setGoalRescopeDrafts] = useState<Map<string, { startDate?: string; deadline?: string; timeBudgetMinutes?: number }>>(new Map());
  const [calendarBookingChoice, setCalendarBookingChoice] = useState<"ai_book" | "save_reference" | null>(null);
  const [currentSchedulingGoalIndex, setCurrentSchedulingGoalIndex] = useState(0);
  
  const { data: goals = [] } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });

  const basicInfoForm = useForm<z.infer<typeof basicInfoSchema>>({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: {
      name: "",
      wakeTime: "07:00",
      sleepTime: "22:00",
      productiveTimeOfDay: "morning",
    },
  });

  const jobForm = useForm<z.infer<typeof jobScheduleSchema>>({
    resolver: zodResolver(jobScheduleSchema),
    defaultValues: {
      dayOfWeek: "",
      startTime: "09:00",
      endTime: "17:00",
      description: "",
    },
  });

  const childForm = useForm<z.infer<typeof childCommitmentSchema>>({
    resolver: zodResolver(childCommitmentSchema),
    defaultValues: {
      description: "",
      dayOfWeek: "",
      startTime: "",
      endTime: "",
    },
  });

  const activityForm = useForm<z.infer<typeof personalActivitySchema>>({
    resolver: zodResolver(personalActivitySchema),
    defaultValues: {
      description: "",
      dayOfWeek: "",
      startTime: "",
      endTime: "",
    },
  });

  const travelForm = useForm<z.infer<typeof travelEventSchema>>({
    resolver: zodResolver(travelEventSchema),
    defaultValues: {
      description: "",
      startDate: "",
      endDate: "",
    },
  });

  const createTimeBudgetMutation = useMutation({
    mutationFn: async () => {
      const basicInfo = basicInfoForm.getValues();
      const budgetData = {
        name: basicInfo.name,
        wakeTime: basicInfo.wakeTime,
        sleepTime: basicInfo.sleepTime,
        productiveTimeOfDay: basicInfo.productiveTimeOfDay,
        hasJob: jobSchedules.length > 0,
        hasChildren: childCommitments.length > 0,
      };

      const response = await apiRequest("POST", "/api/time-budgets", budgetData);
      const budget = await response.json();

      for (const holiday of selectedHolidays) {
        const holidayData = RELIGIOUS_HOLIDAYS.find(h => h.name === holiday);
        await apiRequest("POST", `/api/time-budgets/${budget.id}/religious-holidays`, {
          timeBudgetId: budget.id,
          holidayName: holiday,
          religion: holidayData?.religion,
        });
      }

      for (const schedule of jobSchedules) {
        await apiRequest("POST", `/api/time-budgets/${budget.id}/job-schedules`, {
          timeBudgetId: budget.id,
          ...schedule,
        });
      }

      for (const commitment of childCommitments) {
        await apiRequest("POST", `/api/time-budgets/${budget.id}/child-commitments`, {
          timeBudgetId: budget.id,
          ...commitment,
        });
      }

      for (const activity of personalActivities) {
        await apiRequest("POST", `/api/time-budgets/${budget.id}/personal-activities`, {
          timeBudgetId: budget.id,
          ...activity,
        });
      }

      for (const event of travelEvents) {
        await apiRequest("POST", `/api/time-budgets/${budget.id}/travel-events`, {
          timeBudgetId: budget.id,
          description: event.description,
          startDate: new Date(event.startDate),
          endDate: new Date(event.endDate),
        });
      }

      if (goalAllocations.length > 0) {
        await apiRequest("POST", `/api/time-budgets/${budget.id}/goals/bulk`, {
          allocations: goalAllocations.map((a, idx) => ({
            goalId: a.goalId,
            requiredMinutesPerWeek: a.requiredMinutesPerWeek,
            allocatedMinutesPerWeek: a.allocatedMinutesPerWeek,
            preferredDays: a.preferredDays,
            scheduledBlocks: a.scheduledBlocks,
            allocationStatus: a.allocatedMinutesPerWeek >= a.requiredMinutesPerWeek ? "allocated" : "partial",
            bookOnCalendar: calendarBookingChoice === "ai_book",
            sortOrder: idx,
          })),
        });
      }

      return budget;
    },
    onSuccess: () => {
      toast({
        title: "Time Budget Created!",
        description: "Your time budget has been set up successfully."
      });
      queryClient.invalidateQueries({ queryKey: ["/api/time-budgets"] });
      onComplete();
      onClose();
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create time budget",
        variant: "destructive"
      });
    }
  });

  const resetForm = () => {
    setStep(1);
    basicInfoForm.reset();
    setSelectedHolidays([]);
    setHasJobAnswer(null);
    setJobSchedules([]);
    setChildCommitments([]);
    setPersonalActivities([]);
    setTravelEvents([]);
    setChildActivityDescription("");
    setChildActivityDays([]);
    setCollapsedChildActivities(new Set());
    setPersonalActivityDescription("");
    setPersonalActivityDays([]);
    setCollapsedPersonalActivities(new Set());
    setCollapsedTravelEvents(new Set());
    setSelectedGoalIds([]);
    setGoalAllocations([]);
    setAnalysis(null);
    setIsAnalyzing(false);
    setActivityAdjustments({ sleepAdjustment: 0, workAdjustment: 0, childAdjustment: 0, personalAdjustment: 0, travelAdjustment: 0 });
    setGoalRescopeDrafts(new Map());
    setCalendarBookingChoice(null);
    setCurrentSchedulingGoalIndex(0);
  };

  const addJobSchedule = (data: z.infer<typeof jobScheduleSchema>) => {
    setJobSchedules([...jobSchedules, data]);
    jobForm.reset();
  };

  const addChildCommitmentFromDays = () => {
    if (!childActivityDescription.trim() || childActivityDays.length === 0) {
      toast({
        title: "Missing Information",
        description: "Please enter a description and select at least one day",
        variant: "destructive"
      });
      return;
    }
    const newCommitments = childActivityDays.map(day => ({
      description: childActivityDescription,
      dayOfWeek: day.dayOfWeek,
      startTime: day.startTime,
      endTime: day.endTime
    }));
    setChildCommitments([...childCommitments, ...newCommitments]);
    setChildActivityDescription("");
    setChildActivityDays([]);
    // Collapse the newly added activity
    setCollapsedChildActivities(prev => new Set([...prev, childActivityDescription]));
  };

  const addPersonalActivityFromDays = () => {
    if (!personalActivityDescription.trim() || personalActivityDays.length === 0) {
      toast({
        title: "Missing Information",
        description: "Please enter a description and select at least one day",
        variant: "destructive"
      });
      return;
    }
    const newActivities = personalActivityDays.map(day => ({
      description: personalActivityDescription,
      dayOfWeek: day.dayOfWeek,
      startTime: day.startTime,
      endTime: day.endTime
    }));
    setPersonalActivities([...personalActivities, ...newActivities]);
    setPersonalActivityDescription("");
    setPersonalActivityDays([]);
    // Collapse the newly added activity
    setCollapsedPersonalActivities(prev => new Set([...prev, personalActivityDescription]));
  };

  const addChildCommitment = (data: z.infer<typeof childCommitmentSchema>) => {
    setChildCommitments([...childCommitments, data]);
    childForm.reset();
  };

  const addPersonalActivity = (data: z.infer<typeof personalActivitySchema>) => {
    setPersonalActivities([...personalActivities, data]);
    activityForm.reset();
  };

  const addTravelEvent = (data: z.infer<typeof travelEventSchema>) => {
    setTravelEvents([...travelEvents, data]);
    travelForm.reset();
    // Collapse the newly added event
    setCollapsedTravelEvents(prev => new Set([...prev, travelEvents.length]));
  };
  
  // Group child commitments by description for collapsible display
  const groupedChildCommitments = childCommitments.reduce((acc, commitment) => {
    if (!acc[commitment.description]) {
      acc[commitment.description] = [];
    }
    acc[commitment.description].push(commitment);
    return acc;
  }, {} as Record<string, typeof childCommitments>);
  
  // Group personal activities by description for collapsible display
  const groupedPersonalActivities = personalActivities.reduce((acc, activity) => {
    if (!acc[activity.description]) {
      acc[activity.description] = [];
    }
    acc[activity.description].push(activity);
    return acc;
  }, {} as Record<string, typeof personalActivities>);

  const calculateWeeklyHours = () => {
    const basicInfo = basicInfoForm.getValues();
    const wakeHour = parseInt(basicInfo.wakeTime?.split(":")[0] || "7");
    const sleepHour = parseInt(basicInfo.sleepTime?.split(":")[0] || "22");
    const awakeHoursPerDay = sleepHour > wakeHour ? sleepHour - wakeHour : 24 - wakeHour + sleepHour;
    const totalAwakeHours = awakeHoursPerDay * 7;

    let workHours = 0;
    for (const schedule of jobSchedules) {
      const startHour = parseInt(schedule.startTime.split(":")[0]);
      const endHour = parseInt(schedule.endTime.split(":")[0]);
      workHours += endHour > startHour ? endHour - startHour : 24 - startHour + endHour;
    }

    let childHours = 0;
    for (const commitment of childCommitments) {
      const startHour = parseInt(commitment.startTime.split(":")[0]);
      const endHour = parseInt(commitment.endTime.split(":")[0]);
      childHours += endHour > startHour ? endHour - startHour : 24 - startHour + endHour;
    }

    let activityHours = 0;
    for (const activity of personalActivities) {
      const startHour = parseInt(activity.startTime.split(":")[0]);
      const endHour = parseInt(activity.endTime.split(":")[0]);
      activityHours += endHour > startHour ? endHour - startHour : 24 - startHour + endHour;
    }

    const availableHours = totalAwakeHours - workHours - childHours - activityHours;

    return {
      totalAwakeHours,
      workHours,
      childHours,
      activityHours,
      availableHours: Math.max(0, availableHours),
    };
  };

  const totalSteps = 13;
  const stepTitles = [
    "Basic Info",
    "Religious Holidays",
    "Job Schedule",
    "Child Commitments",
    "Personal Activities",
    "Travel Events",
    "Goal Selection",
    "AI Analysis",
    "Activity Adjustments",
    "Goal Rescoping",
    "Time Block Scheduling",
    "Calendar Decision",
    "Review & Create",
  ];
  
  const activeGoals = goals.filter((g: Goal) => g.status !== "completed" && !isFutureGoal(g));
  
  const handleAnalyzeAllocation = async () => {
    if (selectedGoalIds.length === 0) {
      toast({ title: "Please select at least one goal", variant: "destructive" });
      return;
    }
    
    setIsAnalyzing(true);
    try {
      const basicInfo = basicInfoForm.getValues();
      const response = await apiRequest("POST", `/api/time-budgets/analyze-allocation-preview`, {
        wakeTime: basicInfo.wakeTime,
        sleepTime: basicInfo.sleepTime,
        jobSchedules,
        childCommitments,
        personalActivities,
        travelEvents,
        goalIds: selectedGoalIds,
        activityAdjustments,
      });
      const data = await response.json();
      
      if (data.analysis && !data.analysis.error) {
        setAnalysis(data.analysis);
        
        const newAllocations = data.analysis.recommendations?.map((rec: GoalAllocationAnalysis['recommendations'][0]) => ({
          goalId: rec.goalId,
          goalTitle: rec.goalTitle,
          requiredMinutesPerWeek: rec.suggestedMinutesPerWeek,
          allocatedMinutesPerWeek: rec.suggestedMinutesPerWeek,
          preferredDays: rec.preferredDays || [],
          scheduledBlocks: rec.suggestedTimeSlots?.map((slot: any) => ({
            day: slot.day,
            startTime: slot.startTime,
            endTime: slot.endTime,
          })) || [],
        })) || [];
        setGoalAllocations(newAllocations);
      } else {
        toast({ 
          title: "Analysis incomplete", 
          description: "Could not generate full analysis. Please try again.",
          variant: "destructive" 
        });
      }
    } catch (error) {
      toast({ title: "Analysis failed", description: String(error), variant: "destructive" });
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  const toggleTimeBlock = (goalIndex: number, day: string, timeSlot: string) => {
    const startTimes: Record<string, string> = { morning: "09:00", afternoon: "13:00", evening: "18:00" };
    const endTimes: Record<string, string> = { morning: "12:00", afternoon: "17:00", evening: "21:00" };
    
    setGoalAllocations(prev => {
      const updated = [...prev];
      if (updated[goalIndex]) {
        const blocks = updated[goalIndex].scheduledBlocks;
        const existingIdx = blocks.findIndex(b => b.day.toLowerCase() === day.toLowerCase() && b.startTime === startTimes[timeSlot]);
        if (existingIdx >= 0) {
          updated[goalIndex].scheduledBlocks = blocks.filter((_, i) => i !== existingIdx);
        } else {
          updated[goalIndex].scheduledBlocks = [...blocks, { day, startTime: startTimes[timeSlot], endTime: endTimes[timeSlot] }];
        }
      }
      return updated;
    });
  };
  
  const isBlockSelected = (goalIndex: number, day: string, timeSlot: string) => {
    const startTimes: Record<string, string> = { morning: "09:00", afternoon: "13:00", evening: "18:00" };
    const allocation = goalAllocations[goalIndex];
    if (!allocation) return false;
    return allocation.scheduledBlocks.some(b => b.day.toLowerCase() === day.toLowerCase() && b.startTime === startTimes[timeSlot]);
  };

  const canProceed = () => {
    if (step === 1) {
      return basicInfoForm.formState.isValid;
    }
    return true;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            Create Time Budget
          </DialogTitle>
          <DialogDescription>
            Step {step} of {totalSteps}: {stepTitles[step - 1]}
          </DialogDescription>
        </DialogHeader>

        <Progress value={(step / totalSteps) * 100} className="mb-4" />

        <ScrollArea className="flex-1 pr-4">
          {step === 1 && (
            <Form {...basicInfoForm}>
              <form className="space-y-4">
                <FormField
                  control={basicInfoForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Budget Name</FormLabel>
                      <FormControl>
                        <Input placeholder="My Weekly Schedule" {...field} data-testid="input-budget-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={basicInfoForm.control}
                    name="wakeTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Wake Time</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} data-testid="input-wake-time" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={basicInfoForm.control}
                    name="sleepTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sleep Time</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} data-testid="input-sleep-time" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={basicInfoForm.control}
                  name="productiveTimeOfDay"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Most Productive Time</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-productive-time">
                            <SelectValue placeholder="Select time of day" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="morning">Morning</SelectItem>
                          <SelectItem value="afternoon">Afternoon</SelectItem>
                          <SelectItem value="evening">Evening</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Select religious holidays you observe that may affect your schedule:
              </p>
              
              {/* Quick select by religion */}
              <div className="flex flex-wrap gap-2 pb-2 border-b">
                <span className="text-sm font-medium text-muted-foreground mr-2">Quick select:</span>
                {["Christian", "Jewish", "Islamic", "Hindu", "Buddhist", "Sikh", "Secular"].map((religion) => {
                  const religionHolidayNames = RELIGIOUS_HOLIDAYS.filter(h => h.religion === religion).map(h => h.name as string);
                  const allSelected = religionHolidayNames.every(h => selectedHolidays.includes(h));
                  return (
                    <Button
                      key={religion}
                      type="button"
                      variant={allSelected ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        if (allSelected) {
                          // Deselect all from this religion
                          const holidaySet = new Set(religionHolidayNames);
                          setSelectedHolidays(prev => prev.filter(h => !holidaySet.has(h)));
                        } else {
                          // Select all from this religion
                          setSelectedHolidays(prev => {
                            const combined = [...prev, ...religionHolidayNames];
                            return combined.filter((h, i) => combined.indexOf(h) === i);
                          });
                        }
                      }}
                      data-testid={`button-select-${religion.toLowerCase()}-holidays`}
                    >
                      {religion}
                    </Button>
                  );
                })}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {RELIGIOUS_HOLIDAYS.map((holiday) => (
                  <div
                    key={holiday.name}
                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted cursor-pointer"
                    onClick={() => {
                      setSelectedHolidays(prev =>
                        prev.includes(holiday.name)
                          ? prev.filter(h => h !== holiday.name)
                          : [...prev, holiday.name]
                      );
                    }}
                  >
                    <Checkbox
                      checked={selectedHolidays.includes(holiday.name)}
                      data-testid={`checkbox-holiday-${holiday.name.toLowerCase().replace(/\s+/g, '-')}`}
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium">{holiday.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">({holiday.religion})</span>
                    </div>
                  </div>
                ))}
              </div>
              {selectedHolidays.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2">Selected: {selectedHolidays.length} holidays</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedHolidays.map(h => (
                      <Badge key={h} variant="secondary" className="flex items-center gap-1">
                        {h}
                        <X
                          className="w-3 h-3 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedHolidays(prev => prev.filter(x => x !== h));
                          }}
                        />
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              {/* First ask if they have a job */}
              {hasJobAnswer === null && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Do you currently have a job that employs you?
                  </p>
                  <p className="text-xs text-muted-foreground">
                    This helps us understand how much time you have available during your work days.
                  </p>
                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setHasJobAnswer("yes")}
                      data-testid="button-has-job-yes"
                    >
                      <Briefcase className="w-4 h-4 mr-2" />
                      Yes, I have a job
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setHasJobAnswer("no")}
                      data-testid="button-has-job-no"
                    >
                      No, I don't have a job
                    </Button>
                  </div>
                </div>
              )}

              {/* If they said no */}
              {hasJobAnswer === "no" && (
                <div className="text-center py-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <Briefcase className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="font-medium">No job schedule to configure</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      You can proceed to the next step.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-3"
                    onClick={() => setHasJobAnswer(null)}
                  >
                    Change answer
                  </Button>
                </div>
              )}

              {/* If they said yes, show the day/time configuration */}
              {hasJobAnswer === "yes" && (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Configure your work schedule</p>
                      <p className="text-sm text-muted-foreground">
                        Select the days you work and set your start and end times for each day.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setHasJobAnswer(null);
                        setJobSchedules([]);
                      }}
                    >
                      Change answer
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {DAYS_OF_WEEK.map((day) => {
                      const existingSchedule = jobSchedules.find(s => s.dayOfWeek === day.value);
                      const isChecked = !!existingSchedule;
                      
                      return (
                        <div key={day.value} className="flex items-center gap-3 p-3 border rounded-lg">
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                // Add this day with default times
                                setJobSchedules(prev => [...prev, {
                                  dayOfWeek: day.value,
                                  startTime: "09:00",
                                  endTime: "17:00",
                                  description: ""
                                }]);
                              } else {
                                // Remove this day
                                setJobSchedules(prev => prev.filter(s => s.dayOfWeek !== day.value));
                              }
                            }}
                            data-testid={`checkbox-job-${day.value}`}
                          />
                          <span className="w-24 font-medium">{day.label}</span>
                          
                          {isChecked && (
                            <>
                              <Input
                                type="time"
                                value={existingSchedule?.startTime || "09:00"}
                                onChange={(e) => {
                                  setJobSchedules(prev => prev.map(s => 
                                    s.dayOfWeek === day.value 
                                      ? { ...s, startTime: e.target.value }
                                      : s
                                  ));
                                }}
                                className="w-28"
                                data-testid={`input-job-start-${day.value}`}
                              />
                              <span className="text-muted-foreground">to</span>
                              <Input
                                type="time"
                                value={existingSchedule?.endTime || "17:00"}
                                onChange={(e) => {
                                  setJobSchedules(prev => prev.map(s => 
                                    s.dayOfWeek === day.value 
                                      ? { ...s, endTime: e.target.value }
                                      : s
                                  ));
                                }}
                                className="w-28"
                                data-testid={`input-job-end-${day.value}`}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  // Copy this day's times to ALL days (select all and apply times)
                                  const sourceSchedule = existingSchedule;
                                  if (sourceSchedule) {
                                    const allDays = [
                                      { value: "monday", label: "Monday" },
                                      { value: "tuesday", label: "Tuesday" },
                                      { value: "wednesday", label: "Wednesday" },
                                      { value: "thursday", label: "Thursday" },
                                      { value: "friday", label: "Friday" },
                                      { value: "saturday", label: "Saturday" },
                                      { value: "sunday", label: "Sunday" },
                                    ];
                                    setJobSchedules(allDays.map(d => ({
                                      dayOfWeek: d.value,
                                      startTime: sourceSchedule.startTime,
                                      endTime: sourceSchedule.endTime
                                    })));
                                  }
                                }}
                                title="Copy times to all days"
                                data-testid={`button-copy-times-${day.value}`}
                              >
                                Copy to All
                              </Button>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {jobSchedules.length > 0 && (
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm font-medium">
                        Work days: {jobSchedules.length} days selected
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Add your children's activities and commitments:
              </p>
              
              {/* Added commitments - collapsible view */}
              {Object.keys(groupedChildCommitments).length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Added commitments:</p>
                  {Object.entries(groupedChildCommitments).map(([description, commitments]) => {
                    const isCollapsed = collapsedChildActivities.has(description);
                    return (
                      <div key={description} className="border rounded-lg overflow-hidden">
                        <div
                          className="flex items-center justify-between p-3 bg-muted cursor-pointer hover:bg-muted/80"
                          onClick={() => {
                            setCollapsedChildActivities(prev => {
                              const next = new Set(prev);
                              if (next.has(description)) {
                                next.delete(description);
                              } else {
                                next.add(description);
                              }
                              return next;
                            });
                          }}
                          data-testid={`toggle-child-${description}`}
                        >
                          <div className="flex items-center gap-2">
                            {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                            <span className="font-medium">{description}</span>
                            <Badge variant="secondary">{commitments.length} day{commitments.length > 1 ? 's' : ''}</Badge>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setChildCommitments(childCommitments.filter(c => c.description !== description));
                            }}
                            data-testid={`button-remove-child-group-${description}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                        {!isCollapsed && (
                          <div className="p-3 space-y-1 bg-background">
                            {commitments.map((commitment, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm">
                                <span className="capitalize">{commitment.dayOfWeek}: {commitment.startTime} - {commitment.endTime}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add new commitment form */}
              <div className="p-4 border rounded-lg space-y-4">
                <div>
                  <label className="text-sm font-medium">Activity Description</label>
                  <Input 
                    placeholder="Soccer practice, School pickup, etc." 
                    value={childActivityDescription}
                    onChange={(e) => setChildActivityDescription(e.target.value)}
                    data-testid="input-child-description"
                    className="mt-1"
                  />
                </div>
                
                {childActivityDescription && (
                  <>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Select Days & Times</label>
                      <p className="text-xs text-muted-foreground mb-2">
                        {childActivityDays.length} day{childActivityDays.length !== 1 ? 's' : ''} selected
                      </p>
                    </div>
                    <div className="space-y-2">
                      {DAYS_OF_WEEK.map(day => {
                        const existingDay = childActivityDays.find(d => d.dayOfWeek === day.value);
                        const isChecked = !!existingDay;
                        return (
                          <div key={day.value} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted">
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setChildActivityDays([...childActivityDays, { dayOfWeek: day.value, startTime: "15:00", endTime: "17:00" }]);
                                } else {
                                  setChildActivityDays(childActivityDays.filter(d => d.dayOfWeek !== day.value));
                                }
                              }}
                              data-testid={`checkbox-child-${day.value}`}
                            />
                            <span className="w-24 font-medium">{day.label}</span>
                            {isChecked && (
                              <>
                                <Input
                                  type="time"
                                  value={existingDay.startTime}
                                  onChange={(e) => {
                                    setChildActivityDays(childActivityDays.map(d => 
                                      d.dayOfWeek === day.value ? { ...d, startTime: e.target.value } : d
                                    ));
                                  }}
                                  className="w-28"
                                  data-testid={`input-child-start-${day.value}`}
                                />
                                <span>to</span>
                                <Input
                                  type="time"
                                  value={existingDay.endTime}
                                  onChange={(e) => {
                                    setChildActivityDays(childActivityDays.map(d => 
                                      d.dayOfWeek === day.value ? { ...d, endTime: e.target.value } : d
                                    ));
                                  }}
                                  className="w-28"
                                  data-testid={`input-child-end-${day.value}`}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const sourceDay = existingDay;
                                    setChildActivityDays(DAYS_OF_WEEK.map(d => ({
                                      dayOfWeek: d.value,
                                      startTime: sourceDay.startTime,
                                      endTime: sourceDay.endTime
                                    })));
                                  }}
                                  title="Copy times to all days"
                                  data-testid={`button-copy-child-times-${day.value}`}
                                >
                                  Copy to All
                                </Button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full" 
                  onClick={addChildCommitmentFromDays}
                  disabled={!childActivityDescription.trim() || childActivityDays.length === 0}
                  data-testid="button-add-child-commitment"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Child Commitment
                </Button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Add recurring personal activities (gym, meditation, hobbies, etc.):
              </p>
              
              {/* Added activities - collapsible view */}
              {Object.keys(groupedPersonalActivities).length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Added activities:</p>
                  {Object.entries(groupedPersonalActivities).map(([description, activities]) => {
                    const isCollapsed = collapsedPersonalActivities.has(description);
                    return (
                      <div key={description} className="border rounded-lg overflow-hidden">
                        <div
                          className="flex items-center justify-between p-3 bg-muted cursor-pointer hover:bg-muted/80"
                          onClick={() => {
                            setCollapsedPersonalActivities(prev => {
                              const next = new Set(prev);
                              if (next.has(description)) {
                                next.delete(description);
                              } else {
                                next.add(description);
                              }
                              return next;
                            });
                          }}
                          data-testid={`toggle-activity-${description}`}
                        >
                          <div className="flex items-center gap-2">
                            {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                            <span className="font-medium">{description}</span>
                            <Badge variant="secondary">{activities.length} day{activities.length > 1 ? 's' : ''}</Badge>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPersonalActivities(personalActivities.filter(a => a.description !== description));
                            }}
                            data-testid={`button-remove-activity-group-${description}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                        {!isCollapsed && (
                          <div className="p-3 space-y-1 bg-background">
                            {activities.map((activity, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm">
                                <span className="capitalize">{activity.dayOfWeek}: {activity.startTime} - {activity.endTime}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add new activity form */}
              <div className="p-4 border rounded-lg space-y-4">
                <div>
                  <label className="text-sm font-medium">Activity</label>
                  <Input 
                    placeholder="Gym, Yoga, Reading, etc." 
                    value={personalActivityDescription}
                    onChange={(e) => setPersonalActivityDescription(e.target.value)}
                    data-testid="input-activity-description"
                    className="mt-1"
                  />
                </div>
                
                {personalActivityDescription && (
                  <>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Select Days & Times</label>
                      <p className="text-xs text-muted-foreground mb-2">
                        {personalActivityDays.length} day{personalActivityDays.length !== 1 ? 's' : ''} selected
                      </p>
                    </div>
                    <div className="space-y-2">
                      {DAYS_OF_WEEK.map(day => {
                        const existingDay = personalActivityDays.find(d => d.dayOfWeek === day.value);
                        const isChecked = !!existingDay;
                        return (
                          <div key={day.value} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted">
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setPersonalActivityDays([...personalActivityDays, { dayOfWeek: day.value, startTime: "18:00", endTime: "19:00" }]);
                                } else {
                                  setPersonalActivityDays(personalActivityDays.filter(d => d.dayOfWeek !== day.value));
                                }
                              }}
                              data-testid={`checkbox-activity-${day.value}`}
                            />
                            <span className="w-24 font-medium">{day.label}</span>
                            {isChecked && (
                              <>
                                <Input
                                  type="time"
                                  value={existingDay.startTime}
                                  onChange={(e) => {
                                    setPersonalActivityDays(personalActivityDays.map(d => 
                                      d.dayOfWeek === day.value ? { ...d, startTime: e.target.value } : d
                                    ));
                                  }}
                                  className="w-28"
                                  data-testid={`input-activity-start-${day.value}`}
                                />
                                <span>to</span>
                                <Input
                                  type="time"
                                  value={existingDay.endTime}
                                  onChange={(e) => {
                                    setPersonalActivityDays(personalActivityDays.map(d => 
                                      d.dayOfWeek === day.value ? { ...d, endTime: e.target.value } : d
                                    ));
                                  }}
                                  className="w-28"
                                  data-testid={`input-activity-end-${day.value}`}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const sourceDay = existingDay;
                                    setPersonalActivityDays(DAYS_OF_WEEK.map(d => ({
                                      dayOfWeek: d.value,
                                      startTime: sourceDay.startTime,
                                      endTime: sourceDay.endTime
                                    })));
                                  }}
                                  title="Copy times to all days"
                                  data-testid={`button-copy-activity-times-${day.value}`}
                                >
                                  Copy to All
                                </Button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full" 
                  onClick={addPersonalActivityFromDays}
                  disabled={!personalActivityDescription.trim() || personalActivityDays.length === 0}
                  data-testid="button-add-activity"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Personal Activity
                </Button>
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Add any upcoming travel that will affect your schedule:
              </p>
              
              {/* Added travel events - collapsible view */}
              {travelEvents.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Added travel events:</p>
                  {travelEvents.map((event, idx) => {
                    const isCollapsed = collapsedTravelEvents.has(idx);
                    return (
                      <div key={idx} className="border rounded-lg overflow-hidden">
                        <div
                          className="flex items-center justify-between p-3 bg-muted cursor-pointer hover:bg-muted/80"
                          onClick={() => {
                            setCollapsedTravelEvents(prev => {
                              const next = new Set(prev);
                              if (next.has(idx)) {
                                next.delete(idx);
                              } else {
                                next.add(idx);
                              }
                              return next;
                            });
                          }}
                          data-testid={`toggle-travel-${idx}`}
                        >
                          <div className="flex items-center gap-2">
                            {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                            <span className="font-medium">{event.description}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTravelEvents(travelEvents.filter((_, i) => i !== idx));
                            }}
                            data-testid={`button-remove-travel-${idx}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                        {!isCollapsed && (
                          <div className="p-3 bg-background">
                            <span className="text-sm text-muted-foreground">
                              {event.startDate} to {event.endDate}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add new travel event form */}
              <Form {...travelForm}>
                <form onSubmit={travelForm.handleSubmit(addTravelEvent)} className="space-y-4 p-4 border rounded-lg">
                  <FormField
                    control={travelForm.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Travel Description</FormLabel>
                        <FormControl>
                          <Input placeholder="Trip to Paris, Visit family, etc." {...field} data-testid="input-travel-description" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={travelForm.control}
                      name="startDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} data-testid="input-travel-start-date" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={travelForm.control}
                      name="endDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} data-testid="input-travel-end-date" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <Button type="submit" variant="outline" className="w-full" data-testid="button-add-travel">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Travel Event
                  </Button>
                </form>
              </Form>
            </div>
          )}

          {step === 7 && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">Select Goals</h3>
                <p className="text-muted-foreground">Choose which goals you want to allocate time for in this budget</p>
              </div>
              
              {activeGoals.length === 0 ? (
                <div className="text-center py-8">
                  <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No active goals found. You can skip this step or create goals first.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {activeGoals.map((goal: Goal) => (
                    <div
                      key={goal.id}
                      className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted cursor-pointer"
                      onClick={() => {
                        setSelectedGoalIds(prev =>
                          prev.includes(goal.id)
                            ? prev.filter(id => id !== goal.id)
                            : [...prev, goal.id]
                        );
                      }}
                      data-testid={`goal-item-${goal.id}`}
                    >
                      <Checkbox
                        checked={selectedGoalIds.includes(goal.id)}
                        data-testid={`checkbox-goal-${goal.id}`}
                      />
                      <div className="flex-1">
                        <p className="font-medium">{goal.title}</p>
                        {goal.description && (
                          <p className="text-sm text-muted-foreground line-clamp-1">{goal.description}</p>
                        )}
                        <div className="flex gap-2 mt-1">
                          {goal.category && <Badge variant="outline" className="text-xs">{goal.category}</Badge>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {selectedGoalIds.length > 0 && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium">Selected: {selectedGoalIds.length} goals</p>
                </div>
              )}
            </div>
          )}

          {step === 8 && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">AI Schedule Analysis</h3>
                <p className="text-muted-foreground">Let AI analyze your schedule and suggest time allocations</p>
              </div>
              
              {!analysis ? (
                <div className="text-center py-8">
                  <Button
                    onClick={handleAnalyzeAllocation}
                    disabled={isAnalyzing || selectedGoalIds.length === 0}
                    data-testid="button-analyze-schedule"
                  >
                    {isAnalyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4 mr-2" />
                        Analyze Schedule
                      </>
                    )}
                  </Button>
                  {selectedGoalIds.length === 0 && (
                    <p className="text-sm text-muted-foreground mt-2">Go back and select at least one goal</p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">Analysis Results</p>
                    <Badge variant={
                      analysis.overallFeasibility === "feasible" ? "default" :
                      analysis.overallFeasibility === "challenging" ? "secondary" : "destructive"
                    } data-testid="badge-feasibility">
                      {analysis.overallFeasibility}
                    </Badge>
                  </div>
                  
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-muted-foreground">{analysis.summary}</p>
                    </CardContent>
                  </Card>
                  
                  {analysis.totalAvailableMinutesPerWeek && (
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm">
                        Available time: <span className="font-medium">{Math.round(analysis.totalAvailableMinutesPerWeek / 60)} hours/week</span>
                      </p>
                    </div>
                  )}
                  
                  {analysis.conflicts && analysis.conflicts.length > 0 && (
                    <div className="space-y-2">
                      <p className="font-medium flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-destructive" />
                        Conflicts Detected
                      </p>
                      {analysis.conflicts.map((conflict, idx) => (
                        <div key={idx} className="p-3 border border-destructive/20 rounded-lg bg-destructive/5">
                          <p className="text-sm font-medium">{conflict.description}</p>
                          <p className="text-xs text-muted-foreground mt-1">{conflict.suggestedResolution}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {analysis.recommendations && analysis.recommendations.length > 0 && (
                    <div className="space-y-2">
                      <p className="font-medium">Recommendations</p>
                      {analysis.recommendations.map((rec, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex justify-between items-start">
                            <p className="font-medium">{rec.goalTitle}</p>
                            <Badge variant="outline">{Math.round(rec.suggestedMinutesPerWeek / 60 * 10) / 10}h/week</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{rec.reasoning}</p>
                          {rec.preferredDays && rec.preferredDays.length > 0 && (
                            <div className="flex gap-1 mt-2">
                              {rec.preferredDays.map(day => (
                                <Badge key={day} variant="secondary" className="text-xs">{day}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <Button
                    variant="outline"
                    onClick={() => {
                      setAnalysis(null);
                      handleAnalyzeAllocation();
                    }}
                    data-testid="button-reanalyze"
                  >
                    <Wand2 className="w-4 h-4 mr-2" />
                    Re-analyze
                  </Button>
                </div>
              )}
            </div>
          )}

          {step === 9 && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">Activity Adjustments</h3>
                <p className="text-muted-foreground">Adjust your commitments to free up more time</p>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={activityAdjustments.sleepAdjustment !== 0}
                        onCheckedChange={(checked) => {
                          if (!checked) setActivityAdjustments(prev => ({ ...prev, sleepAdjustment: 0 }));
                        }}
                        data-testid="checkbox-adjust-sleep"
                      />
                      <span className="font-medium">Adjust Sleep Schedule</span>
                    </div>
                    <span className="text-sm text-muted-foreground">{activityAdjustments.sleepAdjustment > 0 ? '+' : ''}{activityAdjustments.sleepAdjustment}h</span>
                  </div>
                  <input
                    type="range"
                    min="-2"
                    max="2"
                    step="0.5"
                    value={activityAdjustments.sleepAdjustment}
                    onChange={(e) => setActivityAdjustments(prev => ({ ...prev, sleepAdjustment: parseFloat(e.target.value) }))}
                    className="w-full"
                    data-testid="slider-sleep-adjustment"
                  />
                </div>
                
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={activityAdjustments.workAdjustment !== 0}
                        onCheckedChange={(checked) => {
                          if (!checked) setActivityAdjustments(prev => ({ ...prev, workAdjustment: 0 }));
                        }}
                        data-testid="checkbox-adjust-work"
                      />
                      <span className="font-medium">Reduce Work Hours</span>
                    </div>
                    <span className="text-sm text-muted-foreground">{activityAdjustments.workAdjustment}h</span>
                  </div>
                  <input
                    type="range"
                    min="-4"
                    max="0"
                    step="0.5"
                    value={activityAdjustments.workAdjustment}
                    onChange={(e) => setActivityAdjustments(prev => ({ ...prev, workAdjustment: parseFloat(e.target.value) }))}
                    className="w-full"
                    data-testid="slider-work-adjustment"
                  />
                </div>
                
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={activityAdjustments.childAdjustment !== 0}
                        onCheckedChange={(checked) => {
                          if (!checked) setActivityAdjustments(prev => ({ ...prev, childAdjustment: 0 }));
                        }}
                        data-testid="checkbox-adjust-child"
                      />
                      <span className="font-medium">Adjust Child Commitment Hours</span>
                    </div>
                    <span className="text-sm text-muted-foreground">{activityAdjustments.childAdjustment}h</span>
                  </div>
                  <input
                    type="range"
                    min="-2"
                    max="0"
                    step="0.5"
                    value={activityAdjustments.childAdjustment}
                    onChange={(e) => setActivityAdjustments(prev => ({ ...prev, childAdjustment: parseFloat(e.target.value) }))}
                    className="w-full"
                    data-testid="slider-child-adjustment"
                  />
                </div>
                
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={activityAdjustments.personalAdjustment !== 0}
                        onCheckedChange={(checked) => {
                          if (!checked) setActivityAdjustments(prev => ({ ...prev, personalAdjustment: 0 }));
                        }}
                        data-testid="checkbox-adjust-personal"
                      />
                      <span className="font-medium">Reduce Personal Activities</span>
                    </div>
                    <span className="text-sm text-muted-foreground">{activityAdjustments.personalAdjustment}h</span>
                  </div>
                  <input
                    type="range"
                    min="-4"
                    max="0"
                    step="0.5"
                    value={activityAdjustments.personalAdjustment}
                    onChange={(e) => setActivityAdjustments(prev => ({ ...prev, personalAdjustment: parseFloat(e.target.value) }))}
                    className="w-full"
                    data-testid="slider-personal-adjustment"
                  />
                </div>
                
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={activityAdjustments.travelAdjustment !== 0}
                        onCheckedChange={(checked) => {
                          if (!checked) setActivityAdjustments(prev => ({ ...prev, travelAdjustment: 0 }));
                        }}
                        data-testid="checkbox-adjust-travel"
                      />
                      <span className="font-medium">Reduce Travel Time</span>
                    </div>
                    <span className="text-sm text-muted-foreground">{activityAdjustments.travelAdjustment}h</span>
                  </div>
                  <input
                    type="range"
                    min="-2"
                    max="0"
                    step="0.5"
                    value={activityAdjustments.travelAdjustment}
                    onChange={(e) => setActivityAdjustments(prev => ({ ...prev, travelAdjustment: parseFloat(e.target.value) }))}
                    className="w-full"
                    data-testid="slider-travel-adjustment"
                  />
                </div>
              </div>
              
              <Button
                onClick={() => {
                  setAnalysis(null);
                  handleAnalyzeAllocation();
                  setStep(8);
                }}
                disabled={isAnalyzing}
                data-testid="button-reanalyze-adjustments"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Re-analyzing...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Re-analyze with Adjustments
                  </>
                )}
              </Button>
            </div>
          )}

          {step === 10 && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">Goal Rescoping</h3>
                <p className="text-muted-foreground">Modify your goals to make the schedule more feasible</p>
              </div>
              
              <div className="space-y-4">
                {selectedGoalIds.map((goalId) => {
                  const goal = goals.find((g: Goal) => g.id === goalId);
                  if (!goal) return null;
                  const draft = goalRescopeDrafts.get(goalId) || {};
                  
                  return (
                    <div key={goalId} className="p-4 border rounded-lg space-y-3">
                      <p className="font-medium">{goal.title}</p>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm text-muted-foreground">Start Date</label>
                          <Input
                            type="date"
                            value={draft.startDate || ''}
                            onChange={(e) => {
                              const newDrafts = new Map(goalRescopeDrafts);
                              newDrafts.set(goalId, { ...draft, startDate: e.target.value });
                              setGoalRescopeDrafts(newDrafts);
                            }}
                            data-testid={`input-rescope-start-${goalId}`}
                          />
                        </div>
                        <div>
                          <label className="text-sm text-muted-foreground">Deadline</label>
                          <Input
                            type="date"
                            value={draft.deadline || ''}
                            onChange={(e) => {
                              const newDrafts = new Map(goalRescopeDrafts);
                              newDrafts.set(goalId, { ...draft, deadline: e.target.value });
                              setGoalRescopeDrafts(newDrafts);
                            }}
                            data-testid={`input-rescope-deadline-${goalId}`}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-sm text-muted-foreground">Time Budget (minutes/week): {draft.timeBudgetMinutes || 60}</label>
                        <input
                          type="range"
                          min="15"
                          max="600"
                          step="15"
                          value={draft.timeBudgetMinutes || 60}
                          onChange={(e) => {
                            const newDrafts = new Map(goalRescopeDrafts);
                            const parsed = parseInt(e.target.value, 10);
                            newDrafts.set(goalId, { ...draft, timeBudgetMinutes: Number.isNaN(parsed) ? (draft.timeBudgetMinutes || 60) : parsed });
                            setGoalRescopeDrafts(newDrafts);
                          }}
                          className="w-full"
                          data-testid={`slider-rescope-time-${goalId}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <Button
                onClick={() => {
                  setAnalysis(null);
                  handleAnalyzeAllocation();
                  setStep(8);
                }}
                disabled={isAnalyzing}
                data-testid="button-reanalyze-rescoped"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Re-analyzing...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Re-analyze with Changes
                  </>
                )}
              </Button>
            </div>
          )}

          {step === 11 && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">Time Block Scheduling</h3>
                <p className="text-muted-foreground">
                  {goalAllocations.length > 0 
                    ? `Scheduling: ${goalAllocations[currentSchedulingGoalIndex]?.goalTitle || 'Goal'} (${currentSchedulingGoalIndex + 1}/${goalAllocations.length})`
                    : 'No goals to schedule'}
                </p>
              </div>
              
              {goalAllocations.length > 0 && goalAllocations[currentSchedulingGoalIndex] && (
                <div className="space-y-4">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="font-medium">{goalAllocations[currentSchedulingGoalIndex].goalTitle}</p>
                    <p className="text-sm text-muted-foreground">
                      Suggested: {Math.round(goalAllocations[currentSchedulingGoalIndex].allocatedMinutesPerWeek / 60 * 10) / 10}h/week
                    </p>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          <th className="p-2 text-left text-sm"></th>
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                            <th key={day} className="p-2 text-center text-sm font-medium">{day}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {['morning', 'afternoon', 'evening'].map(slot => (
                          <tr key={slot}>
                            <td className="p-2 text-sm capitalize">{slot}</td>
                            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                              <td key={day} className="p-1">
                                <button
                                  className={`w-full h-10 rounded border ${
                                    isBlockSelected(currentSchedulingGoalIndex, day, slot)
                                      ? 'bg-primary text-primary-foreground'
                                      : 'bg-background hover:bg-muted'
                                  }`}
                                  onClick={() => toggleTimeBlock(currentSchedulingGoalIndex, day, slot)}
                                  data-testid={`button-block-${day.toLowerCase()}-${slot}`}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="flex gap-2 justify-center">
                    {currentSchedulingGoalIndex > 0 && (
                      <Button
                        variant="outline"
                        onClick={() => setCurrentSchedulingGoalIndex(prev => prev - 1)}
                        data-testid="button-prev-goal"
                      >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Previous Goal
                      </Button>
                    )}
                    {currentSchedulingGoalIndex < goalAllocations.length - 1 ? (
                      <Button
                        onClick={() => setCurrentSchedulingGoalIndex(prev => prev + 1)}
                        data-testid="button-next-goal"
                      >
                        Next Goal
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setStep(12)}
                        data-testid="button-finish-scheduling"
                      >
                        Finish Scheduling
                        <CheckCircle className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </div>
              )}
              
              {goalAllocations.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No goals have been allocated. Go back to run the AI analysis.</p>
                </div>
              )}
            </div>
          )}

          {step === 12 && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">Calendar Decision</h3>
                <p className="text-muted-foreground">How would you like to handle the scheduled time blocks?</p>
              </div>
              
              <div className="space-y-3">
                <div
                  className={`p-4 border rounded-lg cursor-pointer ${calendarBookingChoice === 'ai_book' ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}
                  onClick={() => setCalendarBookingChoice('ai_book')}
                  data-testid="option-ai-book"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      checked={calendarBookingChoice === 'ai_book'}
                      onChange={() => setCalendarBookingChoice('ai_book')}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-medium">Have AI book these time blocks on my calendar</p>
                      <p className="text-sm text-muted-foreground">Calendar events will be created for each scheduled block</p>
                    </div>
                  </div>
                </div>
                
                <div
                  className={`p-4 border rounded-lg cursor-pointer ${calendarBookingChoice === 'save_reference' ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}
                  onClick={() => setCalendarBookingChoice('save_reference')}
                  data-testid="option-save-reference"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      checked={calendarBookingChoice === 'save_reference'}
                      onChange={() => setCalendarBookingChoice('save_reference')}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-medium">Save for reference only (don't add to calendar)</p>
                      <p className="text-sm text-muted-foreground">Your time allocations will be saved but no calendar events created</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 13 && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">Final Review</h3>
                <p className="text-muted-foreground">Review everything before creating your time budget</p>
              </div>

              {(() => {
                const hours = calculateWeeklyHours();
                return (
                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <p className="text-2xl font-bold text-primary">{hours.totalAwakeHours}</p>
                        <p className="text-sm text-muted-foreground">Total Awake Hours/Week</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <p className="text-2xl font-bold text-blue-600">{hours.workHours}</p>
                        <p className="text-sm text-muted-foreground">Work Hours/Week</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <p className="text-2xl font-bold text-orange-600">{hours.childHours + hours.activityHours}</p>
                        <p className="text-sm text-muted-foreground">Committed Hours/Week</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <p className="text-2xl font-bold text-green-600">{hours.availableHours}</p>
                        <p className="text-sm text-muted-foreground">Available Hours/Week</p>
                      </CardContent>
                    </Card>
                  </div>
                );
              })()}

              <Separator />

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4" /> Basic Info
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {basicInfoForm.getValues().name} • Wake: {basicInfoForm.getValues().wakeTime} • 
                    Sleep: {basicInfoForm.getValues().sleepTime} • 
                    Most productive: {basicInfoForm.getValues().productiveTimeOfDay}
                  </p>
                </div>

                {selectedHolidays.length > 0 && (
                  <div>
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <Church className="w-4 h-4" /> Religious Holidays ({selectedHolidays.length})
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {selectedHolidays.map(h => (
                        <Badge key={h} variant="outline" className="text-xs">{h}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {jobSchedules.length > 0 && (
                  <div>
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <Briefcase className="w-4 h-4" /> Work Schedule ({jobSchedules.length} entries)
                    </h4>
                    <div className="text-sm text-muted-foreground">
                      {jobSchedules.map((s, i) => (
                        <span key={i} className="capitalize">
                          {s.dayOfWeek}: {s.startTime}-{s.endTime}{i < jobSchedules.length - 1 ? ", " : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {childCommitments.length > 0 && (
                  <div>
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <Baby className="w-4 h-4" /> Child Commitments ({childCommitments.length})
                    </h4>
                    <div className="text-sm text-muted-foreground">
                      {childCommitments.map((c, i) => (
                        <span key={i}>
                          {c.description} ({c.dayOfWeek}){i < childCommitments.length - 1 ? ", " : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {personalActivities.length > 0 && (
                  <div>
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <Heart className="w-4 h-4" /> Personal Activities ({personalActivities.length})
                    </h4>
                    <div className="text-sm text-muted-foreground">
                      {personalActivities.map((a, i) => (
                        <span key={i}>
                          {a.description} ({a.dayOfWeek}){i < personalActivities.length - 1 ? ", " : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {travelEvents.length > 0 && (
                  <div>
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <Plane className="w-4 h-4" /> Upcoming Travel ({travelEvents.length})
                    </h4>
                    <div className="text-sm text-muted-foreground">
                      {travelEvents.map((t, i) => (
                        <span key={i}>
                          {t.description}: {t.startDate} to {t.endDate}{i < travelEvents.length - 1 ? ", " : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {goalAllocations.length > 0 && (
                  <div>
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4" /> Goal Allocations ({goalAllocations.length})
                    </h4>
                    <div className="space-y-2">
                      {goalAllocations.map((allocation, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                          <span className="text-sm font-medium">{allocation.goalTitle}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              {Math.round(allocation.allocatedMinutesPerWeek / 60 * 10) / 10}h/week
                            </span>
                            {allocation.scheduledBlocks.length > 0 && (
                              <Badge variant="outline" className="text-xs">
                                {allocation.scheduledBlocks.length} blocks
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {calendarBookingChoice && (
                  <div>
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4" /> Calendar Settings
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {calendarBookingChoice === 'ai_book' 
                        ? 'Time blocks will be added to your calendar automatically'
                        : 'Time blocks saved for reference only (not added to calendar)'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="flex justify-between">
          <div>
            {step > 1 && (
              <Button 
                variant="outline" 
                onClick={() => {
                  if (step === 11 && analysis?.overallFeasibility === "feasible") {
                    setStep(8);
                  } else {
                    setStep(step - 1);
                  }
                }} 
                data-testid="button-previous-step"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="button-cancel-budget">
              Cancel
            </Button>
            {step < totalSteps ? (
              <Button 
                onClick={() => {
                  if (step === 1) {
                    basicInfoForm.trigger().then(isValid => {
                      if (isValid) setStep(step + 1);
                    });
                  } else if (step === 3 && hasJobAnswer === null) {
                    return;
                  } else if (step === 8) {
                    if (analysis?.overallFeasibility === "feasible" || analysis?.overallFeasibility === "challenging") {
                      setStep(11);
                    } else if (analysis?.overallFeasibility === "not_feasible") {
                      setStep(9);
                    } else {
                      setStep(step + 1);
                    }
                  } else if (step === 9) {
                    if (analysis?.overallFeasibility === "not_feasible") {
                      setStep(10);
                    } else {
                      setStep(11);
                    }
                  } else if (step === 10) {
                    setStep(11);
                  } else {
                    setStep(step + 1);
                  }
                }}
                disabled={(step === 3 && hasJobAnswer === null) || (step === 8 && !analysis)}
                data-testid="button-next-step"
              >
                {step === 8 && analysis?.overallFeasibility === "not_feasible" ? (
                  <>Adjust Activities<ChevronRight className="w-4 h-4 ml-2" /></>
                ) : step === 8 && analysis ? (
                  <>Continue to Scheduling<ChevronRight className="w-4 h-4 ml-2" /></>
                ) : (
                  <>Next<ChevronRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            ) : (
              <Button
                onClick={() => createTimeBudgetMutation.mutate()}
                disabled={createTimeBudgetMutation.isPending}
                data-testid="button-create-budget"
              >
                {createTimeBudgetMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Create Time Budget
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface DaySchedule {
  day: string;
  dayLabel: string;
  awakeHours: number;
  allocatedHours: number;
  freeHours: number;
  blocks: Array<{
    type: "work" | "child" | "activity";
    description: string;
    startTime: string;
    endTime: string;
    hours: number;
  }>;
}

function calculateTimeInMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + (minutes || 0);
}

function calculateDuration(startTime: string, endTime: string): number {
  const startMinutes = calculateTimeInMinutes(startTime);
  const endMinutes = calculateTimeInMinutes(endTime);
  const duration = endMinutes > startMinutes ? endMinutes - startMinutes : (24 * 60) - startMinutes + endMinutes;
  return duration / 60; // Return hours
}

interface GoalAllocationAnalysis {
  totalAvailableMinutesPerWeek: number;
  freeTimeSlots: Array<{ day: string; startTime: string; endTime: string; durationMinutes: number }>;
  recommendations: Array<{
    goalId: string;
    goalTitle: string;
    suggestedMinutesPerWeek: number;
    preferredDays: string[];
    suggestedTimeSlots: Array<{ day: string; startTime: string; endTime: string }>;
    reasoning: string;
  }>;
  conflicts: Array<{
    type: string;
    description: string;
    affectedGoals: string[];
    suggestedResolution: string;
  }>;
  overallFeasibility: "feasible" | "challenging" | "not_feasible";
  summary: string;
}

function GoalAllocationSection({
  budget,
  totalWeeklyFree
}: {
  budget: TimeBudgetWithDetails;
  totalWeeklyFree: number;
}) {
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<GoalAllocationAnalysis | null>(null);
  const [allocations, setAllocations] = useState<Array<{
    goalId: string;
    goalTitle: string;
    requiredMinutesPerWeek: number;
    allocatedMinutesPerWeek: number;
    preferredDays: string[];
  }>>([]);

  const { data: goals = [] } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });

  const { data: existingAllocations = [] } = useQuery<TimeBudgetGoal[]>({
    queryKey: ["/api/time-budgets", budget.id, "goals"],
    enabled: !!budget.id,
  });

  const saveMutation = useMutation({
    mutationFn: async (allocs: typeof allocations) => {
      await apiRequest("POST", `/api/time-budgets/${budget.id}/goals/bulk`, {
        allocations: allocs.map((a, idx) => ({
          goalId: a.goalId,
          requiredMinutesPerWeek: a.requiredMinutesPerWeek,
          allocatedMinutesPerWeek: a.allocatedMinutesPerWeek,
          preferredDays: a.preferredDays,
          allocationStatus: a.allocatedMinutesPerWeek >= a.requiredMinutesPerWeek ? "allocated" : "partial",
          sortOrder: idx,
        })),
      });
    },
    onSuccess: () => {
      toast({ title: "Goal allocations saved" });
      queryClient.invalidateQueries({ queryKey: ["/api/time-budgets", budget.id, "goals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-budgets", budget.id] });
      setIsDialogOpen(false);
      setAnalysis(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const handleAnalyze = async () => {
    if (selectedGoalIds.length === 0) {
      toast({ title: "Please select at least one goal", variant: "destructive" });
      return;
    }

    setIsAnalyzing(true);
    try {
      const response = await apiRequest("POST", `/api/time-budgets/${budget.id}/analyze-goal-allocation`, {
        goalIds: selectedGoalIds,
      });
      const data = await response.json();
      
      if (data.analysis && !data.analysis.error) {
        setAnalysis(data.analysis);
        
        // Initialize allocations from recommendations
        const newAllocations = data.analysis.recommendations?.map((rec: GoalAllocationAnalysis['recommendations'][0]) => ({
          goalId: rec.goalId,
          goalTitle: rec.goalTitle,
          requiredMinutesPerWeek: rec.suggestedMinutesPerWeek,
          allocatedMinutesPerWeek: rec.suggestedMinutesPerWeek,
          preferredDays: rec.preferredDays || [],
        })) || [];
        setAllocations(newAllocations);
      } else {
        toast({ 
          title: "Analysis incomplete", 
          description: "Could not generate full analysis. Please try again.",
          variant: "destructive" 
        });
      }
    } catch (error) {
      toast({ title: "Analysis failed", description: String(error), variant: "destructive" });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const totalAllocatedMinutes = allocations.reduce((sum, a) => sum + a.allocatedMinutesPerWeek, 0);
  const totalAvailableMinutes = totalWeeklyFree * 60;
  const isOverallocated = totalAllocatedMinutes > totalAvailableMinutes;

  const activeGoals = goals.filter(g => g.status !== "completed" && !isFutureGoal(g));

  return (
    <>
      <div className="mt-4 pt-4 border-t">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium flex items-center gap-2">
            <Target className="w-4 h-4" />
            Goal Allocations
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsDialogOpen(true)}
            data-testid="button-allocate-goals"
          >
            <Plus className="w-3 h-3 mr-1" />
            Allocate Goals
          </Button>
        </div>

        {existingAllocations.length > 0 ? (
          <div className="space-y-2">
            {existingAllocations.map((allocation) => {
              const goal = goals.find(g => g.id === allocation.goalId);
              return (
                <div
                  key={allocation.id}
                  className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-primary" />
                    <span className="font-medium text-sm">{goal?.title || "Unknown Goal"}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">
                      {Math.round(allocation.allocatedMinutesPerWeek / 60 * 10) / 10}h/week
                    </span>
                    <Badge
                      variant={allocation.allocationStatus === "allocated" ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {allocation.allocationStatus}
                    </Badge>
                  </div>
                </div>
              );
            })}
            <div className="text-xs text-muted-foreground mt-2">
              Total: {Math.round(existingAllocations.reduce((sum, a) => sum + a.allocatedMinutesPerWeek, 0) / 60 * 10) / 10}h/week allocated
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No goals allocated yet. Click "Allocate Goals" to assign time from your free hours to your goals.
          </p>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Allocate Goals to Time Budget
            </DialogTitle>
            <DialogDescription>
              Select goals and let AI analyze how to best allocate your available {Math.round(totalWeeklyFree)}h/week
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 pr-4">
            {!analysis ? (
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium mb-3">Select goals to allocate time for:</p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {activeGoals.map((goal) => (
                      <div
                        key={goal.id}
                        className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50"
                        onClick={() => {
                          setSelectedGoalIds(prev =>
                            prev.includes(goal.id)
                              ? prev.filter(id => id !== goal.id)
                              : [...prev, goal.id]
                          );
                        }}
                        data-testid={`goal-select-${goal.id}`}
                      >
                        <Checkbox
                          checked={selectedGoalIds.includes(goal.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedGoalIds(prev => [...prev, goal.id]);
                            } else {
                              setSelectedGoalIds(prev => prev.filter(id => id !== goal.id));
                            }
                          }}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{goal.title}</p>
                          {goal.description && (
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {goal.description}
                            </p>
                          )}
                        </div>
                        {goal.category && (
                          <Badge variant="outline" className="text-xs">
                            {goal.category}
                          </Badge>
                        )}
                      </div>
                    ))}
                    {activeGoals.length === 0 && (
                      <p className="text-muted-foreground text-center py-4">
                        No active goals found. Create some goals first.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleAnalyze}
                    disabled={selectedGoalIds.length === 0 || isAnalyzing}
                    data-testid="button-analyze-allocation"
                  >
                    {isAnalyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4 mr-2" />
                        Analyze & Allocate
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Analysis Summary */}
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge
                      variant={
                        analysis.overallFeasibility === "feasible"
                          ? "default"
                          : analysis.overallFeasibility === "challenging"
                          ? "secondary"
                          : "destructive"
                      }
                    >
                      {analysis.overallFeasibility === "feasible"
                        ? "Feasible"
                        : analysis.overallFeasibility === "challenging"
                        ? "Challenging"
                        : "Not Feasible"}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {Math.round(analysis.totalAvailableMinutesPerWeek / 60)}h available/week
                    </span>
                  </div>
                  <p className="text-sm">{analysis.summary}</p>
                </div>

                {/* Conflicts Warning */}
                {analysis.conflicts && analysis.conflicts.length > 0 && (
                  <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                    <p className="font-medium text-destructive flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4" />
                      Conflicts Detected
                    </p>
                    {analysis.conflicts.map((conflict, idx) => (
                      <div key={idx} className="text-sm mb-2">
                        <p className="font-medium">{conflict.description}</p>
                        <p className="text-muted-foreground">{conflict.suggestedResolution}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Over-allocation Warning */}
                {isOverallocated && (
                  <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <p className="font-medium text-orange-600 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Over-allocated by {Math.round((totalAllocatedMinutes - totalAvailableMinutes) / 60 * 10) / 10}h/week
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Consider reducing time allocation or adjusting your schedule.
                    </p>
                  </div>
                )}

                {/* Allocation Adjustments */}
                <div>
                  <p className="text-sm font-medium mb-3">Adjust time allocations:</p>
                  <div className="space-y-3">
                    {allocations.map((allocation, idx) => (
                      <div key={allocation.goalId} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{allocation.goalTitle}</span>
                          <span className="text-sm text-muted-foreground">
                            {Math.round(allocation.allocatedMinutesPerWeek / 60 * 10) / 10}h/week
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max={totalAvailableMinutes}
                          step="15"
                          value={allocation.allocatedMinutesPerWeek}
                          onChange={(e) => {
                            const parsed = parseInt(e.target.value, 10);
                            if (Number.isNaN(parsed)) return;
                            const newAllocations = [...allocations];
                            newAllocations[idx].allocatedMinutesPerWeek = parsed;
                            setAllocations(newAllocations);
                          }}
                          className="w-full"
                          data-testid={`slider-allocation-${allocation.goalId}`}
                        />
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>0h</span>
                          <span>{Math.round(totalAvailableMinutes / 60)}h</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total Summary */}
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="font-medium">Total Allocated</span>
                  <span className={isOverallocated ? "text-destructive font-bold" : "text-primary font-bold"}>
                    {Math.round(totalAllocatedMinutes / 60 * 10) / 10}h / {Math.round(totalAvailableMinutes / 60)}h available
                  </span>
                </div>
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsDialogOpen(false);
              setAnalysis(null);
              setSelectedGoalIds([]);
              setAllocations([]);
            }}>
              Cancel
            </Button>
            {analysis && (
              <Button
                onClick={() => saveMutation.mutate(allocations)}
                disabled={saveMutation.isPending || allocations.length === 0}
                data-testid="button-save-allocations"
              >
                {saveMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Save Allocations
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TimeBudgetDailyView({
  budget,
  onDelete,
  onSetActive
}: {
  budget: TimeBudgetWithDetails;
  onDelete: () => void;
  onSetActive: () => void;
}) {
  const getDailySchedules = (): DaySchedule[] => {
    const wakeMinutes = calculateTimeInMinutes(budget.wakeTime || "07:00");
    const sleepMinutes = calculateTimeInMinutes(budget.sleepTime || "22:00");
    const awakeHoursPerDay = sleepMinutes > wakeMinutes 
      ? (sleepMinutes - wakeMinutes) / 60 
      : (24 * 60 - wakeMinutes + sleepMinutes) / 60;

    return DAYS_OF_WEEK.map(day => {
      const blocks: DaySchedule["blocks"] = [];
      
      // Add work schedules
      const workSchedules = (budget.jobSchedules || []).filter(s => s.dayOfWeek === day.value);
      workSchedules.forEach(s => {
        const hours = calculateDuration(s.startTime, s.endTime);
        blocks.push({
          type: "work",
          description: s.description || "Work",
          startTime: s.startTime,
          endTime: s.endTime,
          hours
        });
      });

      // Add child commitments
      const childCommitments = (budget.childCommitments || []).filter(c => c.dayOfWeek === day.value);
      childCommitments.forEach(c => {
        const hours = calculateDuration(c.startTime, c.endTime);
        blocks.push({
          type: "child",
          description: c.description,
          startTime: c.startTime,
          endTime: c.endTime,
          hours
        });
      });

      // Add personal activities
      const activities = (budget.personalActivities || []).filter(a => a.dayOfWeek === day.value);
      activities.forEach(a => {
        const hours = calculateDuration(a.startTime, a.endTime);
        blocks.push({
          type: "activity",
          description: a.description,
          startTime: a.startTime,
          endTime: a.endTime,
          hours
        });
      });

      // Sort blocks by start time
      blocks.sort((a, b) => calculateTimeInMinutes(a.startTime) - calculateTimeInMinutes(b.startTime));

      const allocatedHours = blocks.reduce((sum, b) => sum + b.hours, 0);
      const freeHours = Math.max(0, awakeHoursPerDay - allocatedHours);

      return {
        day: day.value,
        dayLabel: day.label,
        awakeHours: awakeHoursPerDay,
        allocatedHours,
        freeHours,
        blocks
      };
    });
  };

  const dailySchedules = getDailySchedules();
  const totalWeeklyAwake = dailySchedules.reduce((sum, d) => sum + d.awakeHours, 0);
  const totalWeeklyAllocated = dailySchedules.reduce((sum, d) => sum + d.allocatedHours, 0);
  const totalWeeklyFree = dailySchedules.reduce((sum, d) => sum + d.freeHours, 0);

  const getBlockColor = (type: string) => {
    switch (type) {
      case "work": return "bg-blue-500/20 border-blue-500 text-blue-700 dark:text-blue-300";
      case "child": return "bg-orange-500/20 border-orange-500 text-orange-700 dark:text-orange-300";
      case "activity": return "bg-purple-500/20 border-purple-500 text-purple-700 dark:text-purple-300";
      default: return "bg-gray-500/20 border-gray-500";
    }
  };

  const getBlockIcon = (type: string) => {
    switch (type) {
      case "work": return <Briefcase className="w-3 h-3" />;
      case "child": return <Baby className="w-3 h-3" />;
      case "activity": return <Heart className="w-3 h-3" />;
      default: return null;
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {budget.name}
            </CardTitle>
            {budget.isActive && (
              <Badge className="bg-green-500 hover:bg-green-600">
                <Star className="w-3 h-3 mr-1" />
                Active
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            {!budget.isActive && (
              <Button variant="outline" size="sm" onClick={onSetActive} data-testid="button-set-active">
                <Star className="w-3 h-3 mr-1" />
                Set Active
              </Button>
            )}
            <Button variant="destructive" size="icon" onClick={onDelete} data-testid="button-delete-budget">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <CardDescription className="flex items-center gap-4 mt-2">
          <span>Wake: {budget.wakeTime}</span>
          <span>Sleep: {budget.sleepTime}</span>
          {budget.productiveTimeOfDay && (
            <span className="capitalize">Best: {budget.productiveTimeOfDay}</span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Weekly Summary */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center p-3 bg-muted rounded-lg">
            <p className="text-2xl font-bold text-primary">{Math.round(totalWeeklyAwake)}h</p>
            <p className="text-xs text-muted-foreground">Awake/Week</p>
          </div>
          <div className="text-center p-3 bg-muted rounded-lg">
            <p className="text-2xl font-bold text-orange-600">{Math.round(totalWeeklyAllocated)}h</p>
            <p className="text-xs text-muted-foreground">Allocated</p>
          </div>
          <div className="text-center p-3 bg-muted rounded-lg">
            <p className="text-2xl font-bold text-green-600">{Math.round(totalWeeklyFree)}h</p>
            <p className="text-xs text-muted-foreground">Free</p>
          </div>
        </div>

        {/* Daily Breakdown */}
        <div className="space-y-3">
          {dailySchedules.map((daySchedule) => (
            <div key={daySchedule.day} className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="font-medium w-24">{daySchedule.dayLabel}</span>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">
                      Awake: {daySchedule.awakeHours.toFixed(1)}h
                    </span>
                    <span className="text-orange-600">
                      Allocated: {daySchedule.allocatedHours.toFixed(1)}h
                    </span>
                    <span className="text-green-600 font-medium">
                      Free: {daySchedule.freeHours.toFixed(1)}h
                    </span>
                  </div>
                </div>
                <div className="w-32">
                  <Progress 
                    value={(daySchedule.allocatedHours / daySchedule.awakeHours) * 100} 
                    className="h-2"
                  />
                </div>
              </div>
              
              {daySchedule.blocks.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {daySchedule.blocks.map((block, idx) => (
                    <div 
                      key={idx}
                      className={`flex items-center gap-1 px-2 py-1 rounded border text-xs ${getBlockColor(block.type)}`}
                    >
                      {getBlockIcon(block.type)}
                      <span>{block.startTime}-{block.endTime}</span>
                      <span className="font-medium">{block.description}</span>
                      <span className="text-muted-foreground">({block.hours.toFixed(1)}h)</span>
                    </div>
                  ))}
                </div>
              )}
              
              {daySchedule.blocks.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">No time blocks - fully available</p>
              )}
            </div>
          ))}
        </div>

        {/* Religious Holidays */}
        {budget.religiousHolidays && budget.religiousHolidays.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm font-medium mb-2 flex items-center gap-2">
              <Church className="w-4 h-4" />
              Observed Holidays
            </p>
            <div className="flex flex-wrap gap-2">
              {budget.religiousHolidays.map((holiday) => (
                <Badge key={holiday.id} variant="secondary" className="text-xs">
                  {holiday.holidayName}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Travel Events */}
        {budget.travelEvents && budget.travelEvents.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm font-medium mb-2 flex items-center gap-2">
              <Plane className="w-4 h-4" />
              Planned Travel
            </p>
            <div className="space-y-1">
              {budget.travelEvents.map((event) => (
                <div key={event.id} className="text-sm text-muted-foreground">
                  {event.description}: {new Date(event.startDate).toLocaleDateString()} - {new Date(event.endDate).toLocaleDateString()}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Goal Allocations Section */}
        <GoalAllocationSection budget={budget} totalWeeklyFree={totalWeeklyFree} />
      </CardContent>
    </Card>
  );
}

export default function TimeBudgetPage() {
  const { toast } = useToast();
  const [isManualDialogOpen, setIsManualDialogOpen] = useState(false);
  const [isAIWizardOpen, setIsAIWizardOpen] = useState(false);
  const [selectedBudgetId, setSelectedBudgetId] = useState<string | null>(null);

  const { data: timeBudgets = [], isLoading } = useQuery<TimeBudget[]>({
    queryKey: ["/api/time-budgets"],
  });

  const { data: selectedBudget, isLoading: detailLoading } = useQuery<TimeBudgetWithDetails>({
    queryKey: ["/api/time-budgets", selectedBudgetId],
    enabled: !!selectedBudgetId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/time-budgets/${id}`);
    },
    onSuccess: () => {
      toast({ title: "Time budget deleted" });
      queryClient.invalidateQueries({ queryKey: ["/api/time-budgets"] });
      setSelectedBudgetId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const activateMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("PUT", `/api/time-budgets/${id}/activate`);
    },
    onSuccess: () => {
      toast({ title: "Time budget set as active" });
      queryClient.invalidateQueries({ queryKey: ["/api/time-budgets"] });
      if (selectedBudgetId) {
        queryClient.invalidateQueries({ queryKey: ["/api/time-budgets", selectedBudgetId] });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading time budgets...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card border-b border-border px-4 md:px-6 lg:px-8 py-4 md:py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground flex items-center gap-2">
              <Clock className="h-5 w-5 md:h-6 md:w-6 flex-shrink-0" />
              <span className="truncate">Time Budget</span>
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-muted-foreground mt-1">
              Understand your realistic available time for achieving your goals
            </p>
          </div>
          <div className="flex gap-2 flex-wrap sm:flex-nowrap">
            <Button
              onClick={() => setIsAIWizardOpen(true)}
              variant="outline"
              className="flex-1 sm:flex-initial"
              data-testid="button-ai-assisted-setup"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              <span className="whitespace-nowrap">AI-Assisted</span>
            </Button>
            <Button
              onClick={() => setIsManualDialogOpen(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground flex-1 sm:flex-initial"
              data-testid="button-manual-setup"
            >
              <Plus className="w-4 h-4 mr-2" />
              <span className="whitespace-nowrap">Manual Setup</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 px-4 md:px-6 lg:px-8 py-4 md:py-6">
        {timeBudgets.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Clock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Time Budgets Yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Create a time budget to understand how much realistic time you have available for your goals. 
                This helps with planning and setting achievable targets.
              </p>
              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={() => setIsAIWizardOpen(true)} data-testid="button-empty-ai-setup">
                  <Wand2 className="w-4 h-4 mr-2" />
                  AI-Assisted Setup
                </Button>
                <Button onClick={() => setIsManualDialogOpen(true)} data-testid="button-empty-manual-setup">
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Manual Setup
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Top Section: Selected Budget Daily View */}
            {selectedBudgetId && selectedBudget ? (
              <TimeBudgetDailyView
                budget={selectedBudget}
                onDelete={() => deleteMutation.mutate(selectedBudgetId)}
                onSetActive={() => activateMutation.mutate(selectedBudgetId)}
              />
            ) : (
              <Card className="bg-muted/50">
                <CardContent className="py-8 text-center">
                  <Calendar className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">
                    Select a time budget below to view its daily breakdown
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Bottom Section: Budget List */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                All Time Budgets ({timeBudgets.length})
              </h3>
              <div className="space-y-2">
                {timeBudgets.map((budget) => (
                  <div
                    key={budget.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all hover:bg-muted ${
                      selectedBudgetId === budget.id ? "ring-2 ring-primary bg-primary/5" : ""
                    } ${budget.isActive ? "border-green-500" : ""}`}
                    onClick={() => setSelectedBudgetId(budget.id)}
                    data-testid={`row-time-budget-${budget.id}`}
                  >
                    <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab flex-shrink-0" />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{budget.name}</span>
                        {budget.isActive && (
                          <Badge className="bg-green-500 hover:bg-green-600 text-xs">
                            <Star className="w-2 h-2 mr-1" />
                            Active
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                        <span>{budget.wakeTime} - {budget.sleepTime}</span>
                        {budget.hasJob && (
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-3 h-3" />
                            Work
                          </span>
                        )}
                        {budget.hasChildren && (
                          <span className="flex items-center gap-1">
                            <Baby className="w-3 h-3" />
                            Children
                          </span>
                        )}
                      </div>
                    </div>

                    {budget.totalAvailableMinutesPerWeek && (
                      <div className="text-right flex-shrink-0">
                        <span className="font-medium text-green-600">
                          {Math.round(budget.totalAvailableMinutesPerWeek / 60)}h
                        </span>
                        <span className="text-xs text-muted-foreground block">free/week</span>
                      </div>
                    )}

                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <CreateTimeBudgetDialog
        isOpen={isManualDialogOpen}
        onClose={() => setIsManualDialogOpen(false)}
        onComplete={() => setIsManualDialogOpen(false)}
      />

      <AITimeBudgetWizard
        isOpen={isAIWizardOpen}
        onClose={() => setIsAIWizardOpen(false)}
        onComplete={() => setIsAIWizardOpen(false)}
      />
    </>
  );
}
